(function (scope) {
  'use strict';

  function select(candidates, types, currentId, limit = 4) {
    const seen = new Set([String(currentId)]);
    const queues = types.filter(type => type !== '').map(type => candidates.filter(item => item.type === type));
    const selected = [];
    while (selected.length < limit && queues.some(queue => queue.length)) {
      for (const queue of queues) {
        let item;
        do { item = queue.shift(); } while (item && seen.has(String(item.id)));
        if (!item) continue;
        seen.add(String(item.id));
        selected.push(item);
        if (selected.length === limit) break;
      }
    }
    return selected;
  }

  if (typeof module === 'object' && module.exports) { module.exports = { select }; return; }
  if (scope.__rrStylePairingsReady) return;
  scope.__rrStylePairingsReady = true;
  const configCache = new Map();
  const collectionCache = new Map();
  const cleanups = new Map();
  const reducedMotion = () => scope.matchMedia('(prefers-reduced-motion: reduce)').matches;

  async function request(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Pairing response unavailable');
      return await response.text();
    } finally { clearTimeout(timeout); }
  }

  async function collectionCards(root, collection) {
    const url = new URL(root.dataset.collectionsUrl + '/' + encodeURIComponent(collection.handle), location.origin);
    url.searchParams.set('section_id', 'rr-pairing-candidates');
    url.searchParams.set('sort_by', 'title-ascending');
    const key = url.href;
    if (!collectionCache.has(key)) collectionCache.set(key, (async () => {
      const cards = [];
      let page = 1;
      while (page) {
        url.searchParams.set('page', String(page));
        const doc = new DOMParser().parseFromString(await request(url.href), 'text/html');
        const result = doc.querySelector('[data-rr-pair-page]');
        if (!result || result.dataset.collectionId !== String(collection.id) || Number(result.dataset.page) !== page || Number(result.dataset.count) > 25000) throw new Error('Invalid pairing collection response');
        for (const template of result.querySelectorAll('template[data-rr-pair-card]')) {
          const price = Number(template.dataset.price);
          if (!template.dataset.id || !template.dataset.type || !Number.isFinite(price) || price < 0) continue;
          cards.push({ id: template.dataset.id, type: template.dataset.type, title: template.dataset.title, price: price / 100, currency: template.dataset.currency, collectionId: String(collection.id), template });
        }
        const next = Number(result.dataset.next);
        if (!Number.isInteger(next) || next < 0 || (next && next !== page + 1)) throw new Error('Invalid pairing pagination');
        page = next;
      }
      cards.sort((a, b) => a.title < b.title ? -1 : a.title > b.title ? 1 : Number(a.id) - Number(b.id));
      return cards;
    })());
    return collectionCache.get(key);
  }

  function track(event, selected, index) {
    if (!Array.isArray(scope.dataLayer)) return;
    const items = selected.map((item, position) => ({ item_id: item.id, item_name: item.title, item_category: item.type, price: item.price, index: index === undefined ? position : index, item_list_id: 'rr_style_pairings', item_list_name: 'Pairs well with these' }));
    scope.dataLayer.push({ ecommerce: null });
    scope.dataLayer.push({ event, ecommerce: { item_list_id: 'rr_style_pairings', item_list_name: 'Pairs well with these', currency: selected[0].currency, items } });
  }

  function activate(root, selected) {
    const grid = root.querySelector('[data-rr-pair-grid]');
    grid.replaceChildren(...selected.map(item => item.template.content.cloneNode(true)));
    bind(root, grid, selected);
  }

  function bind(root, grid, selected) {
    if (window.RRProductCards) window.RRProductCards.init(grid);
    grid.querySelectorAll('img').forEach(img => img.setAttribute('sizes', '(min-width: 1100px) 22vw, (min-width: 750px) 23vw, 76vw'));
    root.hidden = false;
    root.dataset.state = 'ready';
    const prev = root.querySelector('[data-rr-pair-prev]');
    const next = root.querySelector('[data-rr-pair-next]');
    const update = () => {
      const overflow = grid.scrollWidth > grid.clientWidth + 2;
      prev.disabled = !overflow || grid.scrollLeft <= 1;
      next.disabled = !overflow || grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2;
      prev.parentElement.hidden = !overflow;
    };
    const controls = new AbortController();
    const step = direction => grid.scrollBy({ left: direction * (grid.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(grid).columnGap) || 0)), behavior: reducedMotion() ? 'auto' : 'smooth' });
    prev.addEventListener('click', () => step(-1), { signal: controls.signal });
    next.addEventListener('click', () => step(1), { signal: controls.signal });
    grid.addEventListener('scroll', update, { passive: true, signal: controls.signal });
    grid.addEventListener('keydown', event => {
      if (event.target !== grid || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); step(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal: controls.signal });
    grid.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      const item = link && link.closest('[data-rr-pair-item]');
      const index = item ? selected.findIndex(candidate => candidate.id === item.dataset.id) : -1;
      if (index >= 0) track('select_item', [selected[index]], index);
    }, { signal: controls.signal });
    const resize = new ResizeObserver(update);
    resize.observe(grid);
    update();
    const seen = new Set();
    let sectionSeen = false;
    const impressions = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < .5) continue;
        const index = selected.findIndex(candidate => candidate.id === entry.target.dataset.id);
        if (index < 0 || seen.has(index)) continue;
        seen.add(index);
        track('view_item_list', [selected[index]], index);
        if (!sectionSeen && Array.isArray(scope.dataLayer)) {
          scope.dataLayer.push({ event: 'rr_pairings_impression', source_product_id: root.dataset.currentId, recommendation_count: selected.length });
          sectionSeen = true;
        }
        impressions.unobserve(entry.target);
      }
    }, { threshold: .5 });
    grid.querySelectorAll('[data-rr-pair-item]').forEach(item => impressions.observe(item));
    cleanups.set(root, () => { controls.abort(); resize.disconnect(); impressions.disconnect(); });
  }

  function bindRendered(root) {
    if (root.dataset.state) return;
    const grid = root.querySelector('[data-rr-pair-grid]');
    const selected = [...grid.querySelectorAll('[data-rr-pair-item]')].map(item => {
      const price = Number(item.dataset.price);
      return { id: item.dataset.id, type: item.dataset.type, title: item.dataset.title, price: Number.isFinite(price) ? price / 100 : 0, currency: item.dataset.currency };
    });
    if (!selected.length) { root.hidden = true; root.dataset.state = 'empty'; return; }
    bind(root, grid, selected);
  }

  async function load(root) {
    if (root.dataset.state) return;
    root.dataset.state = 'loading';
    try {
      const configUrl = root.dataset.configUrl;
      if (!configCache.has(configUrl)) configCache.set(configUrl, request(configUrl).then(JSON.parse));
      const config = await configCache.get(configUrl);
      if (config.version !== 1 || (config.approval.status !== 'approved' && root.dataset.preview !== 'true')) { root.dataset.state = 'approval_pending'; return; }
      const types = config.rules[root.dataset.productType];
      if (!Array.isArray(types) || !types.length) { root.dataset.state = 'empty'; return; }
      const ids = new Set(JSON.parse(root.dataset.collectionIds).map(String));
      const collections = config.collections.filter(collection => ids.has(String(collection.id)));
      if (!collections.length) { root.dataset.state = 'empty'; return; }
      const candidates = [];
      for (const collection of collections) candidates.push(...await collectionCards(root, collection));
      const selected = select(candidates.filter(candidate => candidate.type !== root.dataset.productType), types, root.dataset.currentId);
      if (!root.isConnected) return;
      if (!selected.length) { root.dataset.state = 'empty'; return; }
      activate(root, selected);
    } catch (error) { root.hidden = true; root.dataset.state = 'error'; }
  }

  function init() {
    const roots = document.querySelectorAll('[data-rr-style-pairings]');
    roots.forEach((root, index) => {
      if (index > 0) { root.hidden = true; return; }
      if (root.dataset.observed) return;
      root.dataset.observed = 'true';
      if (root.dataset.source === 'metafield') { bindRendered(root); return; }
      if (!('IntersectionObserver' in scope)) { load(root); return; }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(root); }
      }, { rootMargin: '600px' });
      observer.observe(root.closest('[data-rr-pairings-slot]'));
      cleanups.set(root, () => observer.disconnect());
    });
  }
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of cleanups) if (event.target.contains(root)) { cleanup(); cleanups.delete(root); }
  });
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})(typeof window === 'undefined' ? globalThis : window);
